import {
  type ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router";
import { useMutation, useQuery, useLazyQuery } from "@apollo/client/react";
import { NotificationEditPage as Component } from "../../../components/pages/notification/NotificationEditPage";
import { toISOStringJST, toDatetimeLocalJST } from "../../../utils/date";
import {
  UPDATE_CS_NOTIFICATION,
  CREATE_CS_NOTIFICATION_IMAGE_UPLOAD_URL,
} from "../../../graphql/mutations";
import {
  GET_CS_NOTIFICATION,
  PREVIEW_CS_NOTIFICATION_MARKDOWN,
} from "../../../graphql/queries";
import { useCheckUserRole } from "../../../hooks/useCheckUserRole";
import { showSnackBar } from "../../../graphql/local-mutations";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export const NotificationEditPage = () => {
  useCheckUserRole();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { data, loading } = useQuery(GET_CS_NOTIFICATION, {
    variables: { id },
    skip: !id,
  });

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");
  const [medicalInstitutionIds, setMedicalInstitutionIds] = useState<string[]>(
    [],
  );
  const [medicalInstitutionIdInput, setMedicalInstitutionIdInput] =
    useState("");
  const [roles, setRoles] = useState<string[]>([]);
  const [positions, setPositions] = useState<string[]>([]);
  const [pdtCodes, setPdtCodes] = useState<string[]>([]);
  const [startFrom, setStartFrom] = useState("");
  const [endAt, setEndAt] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const notification = data?.csNotification;

  // 編集不可のお知らせ（規約同意お知らせ等）は編集ページを開けないようにする
  useEffect(() => {
    if (notification && !notification.isEditable) {
      navigate(`/notifications/${id}`, { replace: true });
    }
  }, [notification, id, navigate]);

  // 通知データをフォームに反映
  useEffect(() => {
    if (notification && !initialized) {
      setTitle(notification.title);
      setBody(notification.body);
      setMedicalInstitutionIds(notification.medicalInstitutionIds ?? []);
      setRoles(notification.roles ?? []);
      setPositions(notification.positions ?? []);
      setPdtCodes(notification.pdtCodes ?? []);
      setStartFrom(
        notification.startFrom
          ? toDatetimeLocalJST(notification.startFrom)
          : "",
      );
      setEndAt(
        notification.endAt ? toDatetimeLocalJST(notification.endAt) : "",
      );
      setInitialized(true);
    }
  }, [notification, initialized]);

  const [updateNotification, { loading: isSubmitting }] = useMutation(
    UPDATE_CS_NOTIFICATION,
  );
  const [createImageUploadUrl] = useMutation(
    CREATE_CS_NOTIFICATION_IMAGE_UPLOAD_URL,
  );
  const [fetchPreview] = useLazyQuery(PREVIEW_CS_NOTIFICATION_MARKDOWN, {
    fetchPolicy: "network-only",
  });
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  const handlePreviewTab = useCallback(async () => {
    if (!body) {
      setPreviewHtml("");
      return;
    }
    setIsPreviewLoading(true);
    try {
      const { data: previewData } = await fetchPreview({
        variables: { body },
      });
      if (previewData?.previewCsNotificationMarkdown) {
        setPreviewHtml(previewData.previewCsNotificationMarkdown);
      }
    } finally {
      setIsPreviewLoading(false);
    }
  }, [body, fetchPreview]);

  const handleImageUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelect = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (file.size > MAX_FILE_SIZE) {
        showSnackBar("ファイルサイズは10MB以下にしてください");
        return;
      }

      setIsUploading(true);
      try {
        const { data: uploadData } = await createImageUploadUrl({
          variables: {
            input: { fileName: file.name, contentType: file.type },
          },
        });

        const result = uploadData?.createCsNotificationImageUploadUrl;
        if (
          !result ||
          result.__typename === "CsNotificationImageUploadUrlError"
        ) {
          showSnackBar("画像アップロードURLの取得に失敗しました");
          return;
        }

        const { url, fields, key } = result;

        const formData = new FormData();
        const parsedFields = JSON.parse(fields);
        for (const [k, v] of Object.entries(parsedFields)) {
          formData.append(k, v as string);
        }
        formData.append("file", file);

        const response = await fetch(url, { method: "POST", body: formData });
        if (!response.ok) {
          showSnackBar("画像のアップロードに失敗しました");
          return;
        }

        const imageMarkdown = `![${file.name}](${key})`;
        const textarea = document.getElementById(
          "notification-body-textarea",
        ) as HTMLTextAreaElement | null;
        if (textarea) {
          const currentValue = textarea.value;
          const start = textarea.selectionStart;
          const end = textarea.selectionEnd;
          const newBody =
            currentValue.substring(0, start) +
            imageMarkdown +
            currentValue.substring(end);
          setBody(newBody);
        } else {
          setBody((prev) => prev + "\n" + imageMarkdown);
        }
      } finally {
        setIsUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    },
    [createImageUploadUrl],
  );

  const toggleArrayValue = (arr: string[], value: string) =>
    arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

  const handleSubmit = useCallback(async () => {
    if (!id) return;
    try {
      await updateNotification({
        variables: {
          input: {
            id,
            title,
            body,
            medicalInstitutionIds,
            roles,
            positions,
            pdtCodes,
            startFrom: toISOStringJST(startFrom),
            endAt: endAt ? toISOStringJST(endAt) : undefined,
          },
        },
      });
      showSnackBar("お知らせを更新しました");
      navigate(`/notifications/${id}`);
    } catch {
      showSnackBar("お知らせの更新に失敗しました");
    }
  }, [
    id,
    title,
    body,
    medicalInstitutionIds,
    roles,
    positions,
    pdtCodes,
    startFrom,
    endAt,
    updateNotification,
    navigate,
  ]);

  const handleAddMedicalInstitutionId = useCallback(() => {
    const trimmed = medicalInstitutionIdInput.trim();
    if (trimmed && !medicalInstitutionIds.includes(trimmed)) {
      setMedicalInstitutionIds((prev) => [...prev, trimmed]);
      setMedicalInstitutionIdInput("");
    }
  }, [medicalInstitutionIdInput, medicalInstitutionIds]);

  if (loading || !notification || !initialized) return null;

  return (
    <Component
      title={title}
      body={body}
      previewHtml={previewHtml}
      medicalInstitutionIds={medicalInstitutionIds}
      medicalInstitutionIdInput={medicalInstitutionIdInput}
      roles={roles}
      positions={positions}
      pdtCodes={pdtCodes}
      startFrom={startFrom}
      endAt={endAt}
      isSubmitting={isSubmitting}
      isUploading={isUploading}
      isPreviewLoading={isPreviewLoading}
      fileInputRef={fileInputRef}
      onTitleChange={(e) => setTitle(e.target.value)}
      onBodyChange={(e) => setBody(e.target.value)}
      onMedicalInstitutionIdInputChange={(e) =>
        setMedicalInstitutionIdInput(e.target.value)
      }
      onAddMedicalInstitutionId={handleAddMedicalInstitutionId}
      onRemoveMedicalInstitutionId={(miId) =>
        setMedicalInstitutionIds((prev) => prev.filter((v) => v !== miId))
      }
      onRoleToggle={(role) => setRoles((prev) => toggleArrayValue(prev, role))}
      onPositionToggle={(pos) =>
        setPositions((prev) => toggleArrayValue(prev, pos))
      }
      onPdtCodeToggle={(code) =>
        setPdtCodes((prev) => toggleArrayValue(prev, code))
      }
      onStartFromChange={(e) => setStartFrom(e.target.value)}
      onEndAtChange={(e) => setEndAt(e.target.value)}
      onImageUploadClick={handleImageUploadClick}
      onFileSelect={handleFileSelect}
      onPreviewTab={handlePreviewTab}
      onSubmit={handleSubmit}
      onCancel={() => navigate(`/notifications/${id}`)}
    />
  );
};
